<template>
    <div class="detail">
        <md-dialog-alert :md-active.sync="error"
            :md-content="errorHandler"
            :md-title="$t('general.warning')"
            :md-confirm-text="$t('actions.close')">
        </md-dialog-alert>

        <div class="order-wrap">
            <div class="order-details">
                <h3>{{ $t('orders.details') }}</h3>
                <v-autocomplete v-if="this.isAdmin && !this.editMode"
                    solo
                    flat
                    background-color=""
                    v-model="selectedOrganization"
                    :label="$tc('organizations.organization', 1)"
                    :items="organizations"
                    :allow-overflow="true"
                    :clearable="true"
                    :hide-no-data="true"
                    :return-object="true"
                    item-text="organization_name"
                    item-value="organization_id"
                    id="selectedOrganization"
                    :search-input.sync="searchedOrganizations"
                    @change="fetchOrganizationUsers"></v-autocomplete>
                <v-select v-if="this.isAdmin && !this.editMode"
                    solo
                    flat
                    :menu-props="{offsetY: true}"
                    background-color=""
                    v-model="selectedUser"
                    :label="$tc('users.user',2)"
                    :items="users"
                    item-value="user_id"
                    id="organizationUsers">
                    <template slot="selection"
                        slot-scope="data">
                        {{ data.item.first_name }} {{ data.item.last_name }}
                    </template>
                    <template slot="item"
                        slot-scope="data">
                        {{ data.item.first_name }} {{ data.item.last_name }}
                    </template>
                </v-select>
                <div class="input-group"
                    :class="{ 'validation-error': dateError }">
                    <flat-pickr v-model="order.delivery_date"
                        :config="dateConfig"
                        :placeholder="$t('orders.delivery-date')"
                        class="form-control"
                        name="date">
                    </flat-pickr>
                    <div class="input-group-btn">
                        <button class="btn btn-default"
                            type="button"
                            :title="$t('general.delivery-date')"
                            data-toggle>
                            <i class="fa fa-calendar-check">
                                <span aria-hidden="true"
                                    class="sr-only">Toggle</span>
                            </i>
                        </button>
                    </div>
                </div>
                <md-field>
                    <label>
                        {{ $t('orders.delivery-address') }}
                    </label>
                    <md-textarea v-model="order.address"
                        disabled>
                    </md-textarea>
                </md-field>
                <md-field md-clearable>
                    <label>{{ $t('orders.order-notes') }}</label>
                    <md-textarea v-model="order.description"
                        @input="$v.order.description.$touch()"
                        md-counter="255"
                        maxlength="255"></md-textarea>
                </md-field>
            </div>
            <div class="order-btns">
                <template v-if="order.products != undefined && order.products.length > 0">
                    <md-button v-if="!editMode"
                        class="md-raised add-to-order"
                        @click="toRoute('/orders/new')">
                        <md-icon>add_shopping_cart</md-icon>
                        {{ $t('orders.add-to-order') }}
                    </md-button>
                    <md-button class="md-raised"
                        @click="toRoute('/orders')">
                        {{ $t('actions.previous') }}
                    </md-button>
                    <md-button v-if="editMode"
                        class="md-raised md-primary"
                        @click="validateForm()">
                        {{ $t('actions.update-order') }}
                    </md-button>
                    <md-button v-else
                        class="md-raised md-primary"
                        @click="validateForm()">
                        {{ $t('actions.confirm-order') }}
                    </md-button>
                </template>
                <template v-else>
                    <p>{{ $t('validations.empty-cart') }}</p>
                    <md-button class="md-raised md-primary button-checkout"
                        @click="toRoute('/orders/new')">
                        {{ $t('orders.new-order') }}
                    </md-button>
                </template>
            </div>
        </div>

        <div v-for="(item, index) in inCart"
            :key="index"
            class="md-layout md-gutter md-alignment-top-left">
            <template v-for="(product, index2) in item.products">
                <checkout-item :units="units"
                    :product-status="productStatus"
                    :item="product"
                    :index="index2"
                    :key="product.uuid"
                    @remove-from-cart="removeItemFromOrder"
                    @update-cart="updateItemInOrder"
                    :edit-mode="editMode">
                </checkout-item>
            </template>
        </div>
    </div>
</template>

<script>
import _ from 'lodash'
import CheckoutItem from '@/components/orders/checkout/Item'
import productsStatusService from '@/components/products/services/productsStatus.js'
import unitsService from '@/components/units/services/'
import calibersService from '@/components/calibers/services/'
import ordersService from '@/components/orders/services/'
import ordersStatusService from '@/components/orders/services/ordersStatus'
import organizationsService from '@/components/organizations/services/organizations'
import usersService from '@/components/users/services/users'
import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import { Portuguese } from 'flatpickr/dist/l10n/pt.js'
import { maxLength } from 'vuelidate/lib/validators'
import OrderStatus from '@/utils/orderStatus'
import Validator from '@/utils/validator'

export default {
    name: 'OrderDetail',

    components: {
        CheckoutItem,
        flatPickr
    },

    validations: {
        order: {
            description: {
                maxLength: maxLength(255)
            }
        }
    },

    data () {
        return {
            dateError: false,
            error: false,
            errorHandler: null,

            dateConfig: {
                wrap: true,
                altFormat: 'F j, Y',
                enableTime: true,
                noCalendar: false,
                dateFormat: 'Y-m-d H:i',
                locale: Portuguese
            },

            units: [],
            productStatus: [],
            calibers: [],
            ordersStatus: [],

            selectedOrganization: null,
            organizations: [],
            searchedOrganizations: '',

            selectedUser: null,
            users: []
        }
    },

    props: {
        editMode: {
            type: Boolean,
            default: false
        }
    },

    watch: {
        searchedOrganizations: function (newValue, oldValue) {
            if (newValue !== oldValue) {
                this.fetchOrganizations()
            }
        }
    },

    computed: {
        inCart () {
            if (this.editMode) {
                return this.$store.getters.historicCart
            } else {
                return this.$store.getters.inCart
            }
        },

        order: {
            get () {
                return this.inCart.length > 0 ? this.inCart[0] : []
            },
            set (value) {
                this.value = value
            }
        },

        isAdmin () {
            return this.$store.getters.user.role === 'ADMIN'
        }
    },

    methods: {
        validateForm () {
            let self = this

            if (self.isAdmin && !self.editMode) {
                if (self.selectedOrganization === null || self.selectedOrganization === '' || _.isEmpty(self.selectedOrganization)) {
                    this.errorHandler = this.$t('validations.empty-organization')
                    self.error = true
                    return false
                }

                if (self.selectedUser === null || self.selectedUser === '') {
                    this.errorHandler = this.$t('validations.empty-user')
                    self.error = true
                    return false
                }
            }

            if (self.inCart[0].products.length === 0) {
                this.errorHandler = this.$t('validations.empty-cart')
                self.error = true
            }

            let deliveryDateTime = new Date(self.order.delivery_date).getTime()
            let nowDateTime = new Date().getTime()

            if (deliveryDateTime < nowDateTime) {
                this.errorHandler = this.$t('validations.delivery-date-time')
                self.dateError = true
                self.error = true
            }

            _.forEach(this.inCart[0].products, orderItem => {
                if (!Validator.orderItemIsValid(orderItem)) {
                    this.errorHandler = this.$tc('validations.required-field', 2)
                    self.error = true
                    return false
                }
            })

            if (!self.error) {
                this.errorHandler = null
                this.save()
            }
        },

        async checkDeliveryDate () {
            let self = this

            let deliveryDateTime = new Date(self.order.delivery_date).getTime()
            let nowDateTime = new Date().getTime()

            if (deliveryDateTime < nowDateTime) {
                // adds 24 hours 60 minutes 60 seconds 1000 milliseconds
                self.order.delivery_date = self.makeDeliveryDateTime(nowDateTime + 24 * 60 * 60 * 1000, self.order.preferred_delivery_hour)
            }
        },

        makeDeliveryDateTime (deliveryDate, deliveryTime) {
            let deliveryNewDate = new Date(deliveryDate)
            let year = deliveryNewDate.getFullYear()

            let month = deliveryNewDate.getMonth() + 1
            if (month < 10) {
                month = '0' + month
            }

            let day = deliveryNewDate.getDate()
            if (day < 10) {
                day = '0' + day
            }

            let deliveryHours = deliveryTime.split(':')[0]
            if (deliveryHours.length < 2) {
                deliveryHours = '0' + deliveryHours
            }

            let deliveryMinutes = deliveryTime.split(':')[1]
            if (deliveryMinutes.length < 2) {
                deliveryMinutes = '0' + deliveryMinutes
            }

            let deliveryDateTimeString = year + '-' + month + '-' + day + 'T' + deliveryHours + ':' + deliveryMinutes + ':00'
            return new Date(deliveryDateTimeString)
        },

        getDeliveryTime (deliveryDate) {
            let hours = deliveryDate.split('T')[1].split(':')[0]
            if (hours.length < 2) {
                hours = '0' + hours
            }

            let minutes = deliveryDate.split('T')[1].split(':')[1]
            if (minutes.length < 2) {
                minutes = '0' + minutes
            }

            return hours + ':' + minutes
        },

        async fetchProductStatus () {
            let self = this

            let params = {}

            let response = await productsStatusService.getProductsStatus(params)
            self.productStatus = _.cloneDeep(response.data.results)
        },

        async fetchUnits () {
            let self = this

            let params = {
                filter: {
                    active: 1
                }
            }

            let response = await unitsService.retrieve(params)
            self.units = _.cloneDeep(response.data.results)
        },

        async fetchCalibers () {
            let self = this

            let params = {
                filter: {
                    active: 1
                }
            }

            let response = await calibersService.retrieve(params)
            self.calibers = _.cloneDeep(response.data.results)
        },

        async fetchOrderStatus () {
            let self = this

            let params = {
                filter: {
                    active: 1
                }
            }

            let response = await ordersStatusService.getOrdersStatus(params)
            self.ordersStatus = _.cloneDeep(response.data.results)
        },

        concatOrganizationAddress (organization) {
            return (
                (organization.street ? organization.street + '\n' : '') +
                (organization.zip_code ? organization.zip_code + '\n' : '') +
                (organization.city ? organization.city + '\n' : '')
            )
        },

        async fetchOrganizations () {
            let self = this

            let parameters = {
                pagination: {
                },
                orderBy: {
                    column: 'organization_name',
                    direction: 'ASC'
                },
                filter: {
                    active: 1,
                    organization_name: self.searchedOrganizations === null ? '' : self.searchedOrganizations
                }
            }

            let response = await organizationsService.retrieve(parameters)
            self.organizations = _.cloneDeep(response.data.results)
        },

        async fetchUsers (organizationId) {
            let self = this
            self.users = []

            let params = {
                pagination: {
                },
                orderBy: {
                },
                filter: {
                    active: 1,
                    organization_id: organizationId
                }
            }

            let response = await usersService.getAll(params)
            self.users = _.cloneDeep(response.data.results)
        },

        async fetchOrganizationUsers () {
            let self = this

            // reset users data
            self.users = []
            self.selectedUser = null

            // if a organization is selected
            if (self.selectedOrganization !== undefined) {
                // updates order address to reflect address form text area value
                self.order.address = self.concatOrganizationAddress(self.selectedOrganization)

                // updates delivery time to reflect selected organization form preferred delivery time
                self.order.delivery_date = self.selectedOrganization.preferred_delivery_hour
                self.order.preferred_delivery_hour = self.getDeliveryTime(self.order.delivery_date)
                await self.checkDeliveryDate()

                await self.fetchUsers(self.selectedOrganization.organization_id)
            } else {
                // if organization value was cleared then reset form fields
                self.order.delivery_date = null
                self.order.preferred_delivery_hour = null
                self.order.address = null
            }
        },

        async save () {
            let self = this

            let items = []

            _.forEach(this.inCart[0].products, orderItem => {
                if (!Validator.orderItemIsValid(orderItem)) {
                    this.isValidOrderItem = false
                    return false
                }

                let item = {
                    'product_id': orderItem.product_id,
                    'quantity': orderItem.quantity,
                    'notes': orderItem.notes,
                    'units': orderItem.units,
                    'unit_id': _.find(self.units, { 'description': orderItem.units }).unit_id,
                    'prepared': orderItem.prepared,
                    'product_status_desc': orderItem.product_status,
                    'product_status_id': _.find(self.productStatus, { 'description': orderItem.product_status }).product_status_id,
                    'caliber': orderItem.caliber,
                    'caliber_id': _.find(self.calibers, { 'description': orderItem.caliber }).caliber_id,
                    'active': 1,
                    'item_id': orderItem.item_id,
                    'order_item_status_id': self.mapOrderItemStatus(orderItem)
                }
                items.push(item)
            })

            // to ensure that the delivery date is sent in the iso format to database and be converted to UTC there
            let orderDeliveryDateTime = new Date(this.inCart[0].delivery_date)

            let params = {
                order_id: this.editMode ? this.inCart[0].order_id : null,
                active: this.editMode ? this.inCart[0].active : 1,
                delivery_date: orderDeliveryDateTime,
                description: this.inCart[0].description,
                order_items: items,
                orderstatus_id: this.editMode ? this.inCart[0].orderstatus_id : _.find(self.ordersStatus, { 'description': 'Pendente' }).orderstatus_id,
                user_id: this.editMode ? this.inCart[0].user_id : this.$store.getters.user.userId,
                organization_id: this.editMode ? this.inCart[0].organization_id : this.$store.getters.user.organizationId
            }

            if (this.isAdmin && !this.editMode) {
                params.organization_id = self.selectedOrganization.organization_id
                params.user_id = self.selectedUser
            }

            if (this.editMode) {
                let response = await ordersService.updateWithItems(params)
                if (response.data.order_id) {
                    this.$store.dispatch('emptyHistoricCart')
                    this.$router.push('/orders')
                }
            } else {
                let response = await ordersService.create(params)
                if (response.data.order_id) {
                    self.order = Object.assign(self.order, { products: [], description: '' })
                    this.$store.dispatch('initCart', self.order)
                    this.$router.push('/orders')
                }
            }
        },

        removeItemFromOrder: function (itemData) {
            if (this.editMode) {
                itemData.item.uuid = Date.now()
                itemData.item = Object.assign(itemData.item, { active: 0 })
                this.$store.dispatch('updateHistoricCart', { index: itemData.index, item: itemData.item })
                this.$store.dispatch('removeFromHistoricCart', itemData)
                this.$store.dispatch('loadHistoricCart', itemData)
            } else {
                this.$store.dispatch('removeFromCheckout', itemData)
            }
        },

        updateItemInOrder: function (itemData) {
            if (this.editMode) {
                itemData.item.uuid = Date.now()
                this.$store.dispatch('updateHistoricCart', { index: itemData.index, item: itemData.item })
            } else {
                this.$store.dispatch('updateCart', { index: itemData.index, item: itemData.item })
            }
        },

        mapOrderItemStatus (itemData) {
            if (this.isAdmin) {
                if (!itemData.active) {
                    return OrderStatus.orderItem.REJECTED
                } else if (itemData.selected) {
                    return OrderStatus.orderItem.DELIVERED
                } else {
                    return OrderStatus.orderItem.NOT_DELIVERED
                }
            } else {
                return OrderStatus.orderItem.PENDING
            }
        },

        toRoute (destination) {
            this.$router.push(destination)
        }
    },

    async created () {
        let metadatas = [
            this.fetchProductStatus(),
            this.fetchUnits(),
            this.fetchCalibers(),
            this.fetchOrderStatus()
        ]

        Promise.all(metadatas)

        if (this.isAdmin && !this.editMode) {
            await this.fetchOrganizations()

            // clears address and delivery hour thar were setted on store order
            this.order.delivery_date = ''
            this.order.address = ''
        }

        await this.checkDeliveryDate()
    }
}
</script>
